import type { AppLang } from '@/i18n/locales/i18n.factory';
import { globalTypePath, type EventType } from './geo';

export type LocalizedSeoLang = Exclude<AppLang, 'he'>;

export const LOCALIZED_SERVICE_LANGS = ['en', 'fr', 'ru'] as const satisfies readonly LocalizedSeoLang[];
export const GLOBAL_TYPE_LINK_LANGS = ['he', ...LOCALIZED_SERVICE_LANGS] as const satisfies readonly AppLang[];

export const LOCALIZED_GLOBAL_TYPE_PATHS: Record<LocalizedSeoLang, Record<EventType, string>> = {
  en: {
    חתונה: '/en/dj-for-wedding/',
    חינה: '/en/dj-for-henna/',
    'בר-מצווה': '/en/dj-for-bar-bat-mitzvah/',
    אירוע: '/en/dj-for-event/',
  },
  fr: {
    חתונה: '/fr/dj-mariage/',
    חינה: '/fr/dj-henna/',
    'בר-מצווה': '/fr/dj-bar-bat-mitsva/',
    אירוע: '/fr/dj-evenement/',
  },
  ru: {
    חתונה: '/ru/dj-na-svadbu/',
    חינה: '/ru/dj-na-hennu/',
    'בר-מצווה': '/ru/dj-na-bar-bat-mitzvu/',
    אירוע: '/ru/dj-na-meropriyatie/',
  },
} as const;

const normalizeRoutePath = (pathname: string) => {
  const raw = String(pathname || '/').split(/[?#]/)[0] || '/';
  const withLeadingSlash = raw.startsWith('/') ? raw : `/${raw}`;
  const trimmed = withLeadingSlash.replace(/\/+$/g, '');
  return trimmed || '/';
};

export function getLocalizedGlobalTypePath(
  lang: AppLang | string,
  type: EventType
): string | null {
  if (lang === 'he') return null;
  const langKey = lang as LocalizedSeoLang;
  return LOCALIZED_GLOBAL_TYPE_PATHS[langKey]?.[type] ?? null;
}

export function getGlobalTypePathForLang(
  lang: AppLang | string,
  type: EventType
): string | null {
  if (lang === 'he') return globalTypePath(type);
  return getLocalizedGlobalTypePath(lang, type);
}

export function resolveLocalizedGlobalTypeRoute(
  pathname: string
): { lang: LocalizedSeoLang; type: EventType } | null {
  const normalized = normalizeRoutePath(pathname);

  for (const lang of LOCALIZED_SERVICE_LANGS) {
    const entries = Object.entries(LOCALIZED_GLOBAL_TYPE_PATHS[lang]) as Array<[EventType, string]>;
    const match = entries.find(([, path]) => normalizeRoutePath(path) === normalized);

    if (match) {
      return { lang, type: match[0] };
    }
  }

  return null;
}
